import { dayOf } from '@/lib/investor-analytics';

/**
 * How far a window edge may sit from the close it is measured at. A week
 * covers a weekend plus a holiday; anything further means the index stopped
 * printing (a frozen feed) or started printing late, and the benchmark
 * would be measured over a different span than the row's own return.
 */
const MAX_EDGE_GAP_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days between two `YYYY-MM-DD` days, in either order. */
function daysApart(left: string, right: string): number {
  return Math.abs(
    Math.round(
      (Date.parse(`${left}T00:00:00Z`) - Date.parse(`${right}T00:00:00Z`)) /
        DAY_MS
    )
  );
}

/**
 * An index's return, in percent, over a window, between the same two
 * closes the portfolio's own window return is measured between.
 *
 * - It opens on the last close BEFORE the window's first day. The
 *   portfolio's return for that first day is measured against the prior
 *   day's close, so starting the index at the close OF the first day would
 *   drop that day from one column and not the other.
 * - It closes on the last close on or before the window's last day, the
 *   convention for a series that only prints on trading days: a window that
 *   ends on a Sunday is measured to Friday's close.
 *
 * The series is expected daily; on weekly marks the same rule reaches back
 * up to six days at each end, which is why the period table asks for a
 * daily series.
 *
 * When the index history begins inside the window (a young wallet's
 * benchmark series starts at its first deposit), the first close stands in
 * for the opening edge.
 *
 * Either edge must sit within a week of the window's own edge. Past that
 * the feed stopped (or started late) and the two columns would not cover
 * the same days, so the row has no benchmark figure rather than a wrong one.
 *
 * Ratios are scale invariant, so the anchor the backend rebased the series
 * to does not matter, and an index has no investor flows, so last over
 * first is its true return.
 */
export function benchmarkWindowReturnPct(
  series: readonly { timestamp: string; value: number }[] | undefined,
  window: { from: string | null | undefined; to: string | null | undefined }
): number | null {
  if (!series) return null;
  const closes = series
    .filter((point) => point.value > 0)
    .map((point) => ({ day: dayOf(point.timestamp), value: point.value }))
    .sort((left, right) => left.day.localeCompare(right.day));
  if (closes.length < 2) return null;

  const lastOnOrBefore = (day: string) =>
    closes.findLast((close) => close.day <= day);
  const lastBefore = (day: string) =>
    closes.findLast((close) => close.day < day);

  const fromDay = window.from == null ? undefined : dayOf(window.from);
  const toDay = window.to == null ? undefined : dayOf(window.to);

  const start =
    (fromDay == null ? undefined : lastBefore(fromDay)) ?? closes[0]!;
  const end = toDay == null ? closes.at(-1) : lastOnOrBefore(toDay);
  if (!end || end.day <= start.day) return null;
  if (fromDay != null && daysApart(start.day, fromDay) > MAX_EDGE_GAP_DAYS) {
    return null;
  }
  if (toDay != null && daysApart(end.day, toDay) > MAX_EDGE_GAP_DAYS) {
    return null;
  }
  return (end.value / start.value - 1) * 100;
}
